import { GetAvailableVariables } from '@/api/generated/model';
import { EAggregatedDataType } from '@/api/requests/types/charts.type';
import { MULTIPLE_COLORS, MULTIPLE_COLORS_AMOUNT } from '@/constants/ui.constants';
import { formatChartLabel } from '@/domains/chart/components/create-chart/charts/analytics/chart.utils';
import {
  hasCustomVariableUnitSuffix,
  resolveCustomVariableLabel,
} from '@/domains/chart/components/create-chart/charts/analytics/custom-variables.chart.utils';
import { themeColors } from '@/theme/palette';
import {
  type ChartOptions,
  InteractionMode,
  TooltipItem,
  ChartDataset,
  LegendOptions,
  LegendItem,
  TooltipOptions,
  Chart,
} from 'chart.js';

import { breakdownToTKey } from '@/utils/breakdown-to-translation-key';
import { getDecimalToPercentage } from '@/utils/numbers.util';
import { formatPercentage } from '@/utils/format.util';
import { translate } from '@/utils/translations';
import { capitalize } from 'lodash';
import {
  TooltipAfterBody,
  TooltipTitle,
  TooltipTitleFn,
} from '@/comps/component-library/charts/line-chart/line-chart.util';
import { KindAliasFn } from '@/contexts/alias';
import { Duration } from '@/lib/duration';

export const TOOLTIP_ANIMATION_DURATION = 250;
export const MAX_LEGEND_LABEL_LENGTH = 30;
export const MAX_TOOLTIP_LABEL_WORDS_PER_LINE = 10;
export const CHART_FONT_SIZE = 12;
export const CHART_TICKS_FONT_COLOR = '#93939f';
export const CHART_DATALABELS_FONT_COLOR = '#93939f';
export const X_AXIS_TICKS_CONFIG = {
  stepSize: 1,
  autoSkip: true,
  autoSkipPadding: 5,
  maxRotation: 0,
  display: true,
  color: CHART_TICKS_FONT_COLOR,
  font: {
    size: CHART_FONT_SIZE,
  },
};

export const Y_AXIS_TICKS_CALLBACK = {
  default: (label: string | number) => (label as string) + '%',
  percentage: (label: string | number) =>
    formatChartLabel({
      label: getDecimalToPercentage(+label).toString(),
      variable: EAggregatedDataType.PERCENTAGE,
    }),
  percentageDecimal: (label: string | number) => {
    return formatPercentage(+label, { inputScale: 'decimal' });
  },
};

export const SIX_HOURS_IN_MS = Duration.from({ hours: 6 }).toMillis();

/** Deep-partial tooltip options compatible with `ChartOptions<'line'>['plugins']['tooltip']` */
export type AnalyticsLineChartTooltipProps = NonNullable<NonNullable<ChartOptions<'line'>['plugins']>['tooltip']>;

export const legend = {
  labels: {
    font: {
      size: CHART_FONT_SIZE,
    },
    color: themeColors.text.secondary,
    usePointStyle: true,
    pointStyle: 'circle',
    pointStyleWidth: 0,
    boxWidth: 4,
    boxHeight: 4,
  },
  maxHeight: 54,
};

export const TOOLTIP_STYLES_ANALYTICS: AnalyticsLineChartTooltipProps & {
  // TODO: is this prop correct?
  borderRadius: number;
} = {
  enabled: true,
  borderColor: themeColors.grey[700],
  borderWidth: 1,
  borderRadius: 6,
  backgroundColor: themeColors.background.secondary,
  padding: {
    x: 14,
    y: 8,
  },
  caretPadding: 14, // space between the datapoint and the tooltip
  titleColor: themeColors.text.primary,
  titleFont: {
    size: 12,
    weight: 500,
  },
  bodyFont: {
    size: 10,
    style: 'italic',
    weight: 400,
    lineHeight: 1.2,
  },
  caretSize: 0,
  bodyColor: themeColors.grey[400],
  footerFont: {
    size: 10,
    style: 'normal',
    weight: 400,
  },
  footerColor: themeColors.text.primary,
};

/**
 * Larger, more legible tooltip body font used for descriptive tooltips (e.g. AI proficiency
 * cohort descriptions) whose text is long and needs to be readable across multiple wrapped lines.
 */
export const TOOLTIP_BODY_FONT_LARGE = {
  size: 12,
  style: 'italic' as const,
  weight: 400 as const,
  lineHeight: 1.4,
};

export const legendWithLabels = ({ colors = undefined, items }: { colors?: string[]; items?: string[] }) =>
  ({
    ...legend,
    labels: {
      ...legend.labels,
      generateLabels(chart: Chart): LegendItem[] {
        const { datasets = [] } = chart.data;

        if (items && items.length > 0) {
          const finalItems = items.slice(0, 10).map((label, index) => {
            const datasetColor = datasets[0]?.borderColor;
            const color = Array.isArray(datasetColor)
              ? datasetColor[index]
              : datasetColor ||
                (colors ? colors[index % colors.length] : MULTIPLE_COLORS[index % MULTIPLE_COLORS_AMOUNT] || '');

            // truncate the label if it's too long
            const originalLabel = label || '';
            const legendLabel =
              originalLabel.length > MAX_LEGEND_LABEL_LENGTH
                ? `${originalLabel.slice(0, MAX_LEGEND_LABEL_LENGTH)}...`
                : originalLabel;

            return {
              text: capitalize(legendLabel),
              fontColor: themeColors.text.secondary,
              fillStyle: color,
              lineWidth: 0,
            };
          });
          if (items.length > 10) {
            finalItems.push({
              text: `... +${items.length - 10} more`,
              fontColor: themeColors.text.secondary,
              fillStyle: 'transparent',
              lineWidth: 0,
            });
          }
          return finalItems;
        }

        return datasets.map((dataset, index) => {
          const color = colors ? colors[index % colors.length] : MULTIPLE_COLORS[index % MULTIPLE_COLORS_AMOUNT] || '';

          // truncate the label if it's too long
          const originalLabel = dataset.label || '';
          const label =
            originalLabel.length > MAX_LEGEND_LABEL_LENGTH
              ? `${originalLabel.slice(0, MAX_LEGEND_LABEL_LENGTH)}...`
              : originalLabel;

          return {
            text: capitalize(label),
            fontColor: themeColors.text.secondary,
            fillStyle: color,
            lineWidth: 0,
          };
        });
      },
    },
  }) as unknown as LegendOptions<'line'>;

export const commonOptions = {
  responsive: true,
  maintainAspectRatio: false,
  transitions: {
    zoom: {
      animation: {
        duration: 100,
      },
    },
  },
  animation: {
    duration: 200,
  },
  tension: 0.3,
  hover: {
    mode: 'nearest' as InteractionMode,
  },
  interaction: {
    intersect: false,
  },
};

// export const generateLineKindTooltipUpdated = ({
//   datasets,
//   customText,
//   customPrefix,
//   isRawLabel,
//   breakdown,
// }: {
//   datasets?: ChartDataset<'line'>[];
//   customText?: string;
//   breakdown?: string;
//   customPrefix?: string;
//   isRawLabel?: boolean;
// }): TooltipOptions<'line'> => ({
//   ...TOOLTIP_STYLES_UPDATED,
//   animation: {
//     duration: TOOLTIP_ANIMATION_DURATION,
//   },
//   mode: 'nearest',
//   displayColors: false,
//   // eslint-disable-next-line @typescript-eslint/ban-ts-comment
//   // @ts-ignore
//   callbacks: {
//     beforeTitle(tooltipItems: TooltipItem<'line'>[]): string | string[] | void {
//       const [tooltipItem] = tooltipItems;
//
//       if (isRawLabel) {
//         return `${customPrefix || ''} ${tooltipItem.label} ${pluralize(
//           customText || '',
//           Number(tooltipItem.label ?? 0),
//         )}`;
//       }
//
//       if (!customPrefix) {
//         return chartDateFormatter(new Date(tooltipItem.parsed.x), 'day');
//       }
//
//       return `${customPrefix || ''} ${tooltipItem.parsed.x} ${customText || ''}`;
//     },
//     title() {
//       return '';
//     },
//     label(tooltipItem: TooltipItem<'line'>) {
//       if (!breakdown) {
//         return 'Total';
//       }
//
//       return ` ${capitalize(translate(breakdownToTKey(breakdown), tooltipItem.label))}`;
//     },
//     afterBody(tooltipItems: TooltipItem<'line'>[]) {
//       const [tooltipItem] = tooltipItems;
//       // @ts-expect-error types failure
//       const usersCount = datasets?.[0].usersData?.[tooltipItem.dataIndex];
//       return usersCount ? `${usersCount} users` : '';
//     },
//   },
// });

export const generateBarKindTooltip = ({
  variable,
  overallMaxValue,
  breakdown,
  variableToDisplay,
  skipFormat,
  timeRange,
  tooltipTitle,
  tooltipAfterBody,
  isPercentage,
  isPercentageVariable,
  resolveTooltipVariable,
  colors,
  availableVariables,
  alias,
}: {
  variable: string;
  overallMaxValue?: number;
  breakdown?: string;
  variableToDisplay?: string | TooltipTitleFn<'bar'>;
  timeRange: string;
  tooltipTitle?: TooltipTitle<'bar'>;
  tooltipAfterBody?: TooltipAfterBody<'bar'>;
  skipFormat?: EAggregatedDataType[];
  isPercentage?: boolean;
  isPercentageVariable?: (variable?: string) => boolean;
  resolveTooltipVariable?: (tooltipItem: TooltipItem<'bar'>) => string;
  colors?: string[];
  availableVariables?: GetAvailableVariables[];
  alias?: KindAliasFn;
}): TooltipOptions<'bar'> => ({
  ...TOOLTIP_STYLES_ANALYTICS,
  animation: {
    duration: TOOLTIP_ANIMATION_DURATION,
  },
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  callbacks: {
    beforeTitle(items): string | string[] | void {
      if (variableToDisplay) {
        if (typeof variableToDisplay === 'function') {
          return variableToDisplay(items);
        }
      }

      return `${variableToDisplay || 'Not Selected'}`;
    },
    title() {
      return '';
    },
    labelColor: (tooltipItem: TooltipItem<'bar'>) => {
      const index = tooltipItem.dataIndex;
      const finalColors = colors ? colors : MULTIPLE_COLORS;

      // TODO: fix ignore
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const backgroundColor = finalColors[index % finalColors.length];

      return {
        borderColor: backgroundColor,
        backgroundColor: backgroundColor,
        borderWidth: 2,
        borderRadius: 1,
      };

      // return {
      //   borderColor: MULTIPLE_COLORS[tooltipItem.datasetIndex % MULTIPLE_COLORS_AMOUNT],
      //   borderWidth: 2,
      //   borderRadius: 1,
      //   backgroundColor: MULTIPLE_COLORS[tooltipItem.datasetIndex % MULTIPLE_COLORS_AMOUNT],
      // };
    },
    label(tooltipItem: TooltipItem<'bar'>) {
      if (!breakdown) {
        return 'Total';
      }

      return ` ${capitalize(translate(breakdownToTKey(breakdown), tooltipItem.label))}`;
    },
    afterBody(context) {
      if (tooltipAfterBody && typeof tooltipAfterBody === 'function') {
        return tooltipAfterBody(context);
      }
      return `${breakdown ? (alias?.(breakdown) ?? `${breakdown}`) : ''}`;
    },
    footer([tooltipItem]: TooltipItem<'bar'>[]) {
      const currentValue = tooltipItem.dataset.data[tooltipItem.dataIndex] as number;
      const tooltipVariable = resolveTooltipVariable?.(tooltipItem) ?? variable;
      const showAsPercentage = isPercentage || (isPercentageVariable?.(tooltipVariable) ?? false);

      // If isPercentage is true, show only the percentage value
      if (showAsPercentage) {
        return formatPercentage(currentValue, { inputScale: 'decimal' });
      }

      const translatedVariable = translate('variable', tooltipVariable).toLowerCase();
      const availableVariablesBySlug = availableVariables
        ? new Map(availableVariables.map((entry) => [entry.slug, entry]))
        : null;
      const hideSuffix =
        !!availableVariablesBySlug && hasCustomVariableUnitSuffix(tooltipVariable, availableVariablesBySlug);
      const displayVariable =
        availableVariablesBySlug && !hideSuffix
          ? resolveCustomVariableLabel(tooltipVariable, availableVariablesBySlug, translatedVariable)
          : translatedVariable;
      const percentageFromMaxValue = formatPercentage((currentValue / (overallMaxValue ?? 1)) * 100, {
        inputScale: 'percent',
        showSymbol: false,
      });
      const label = formatChartLabel({
        label: String(currentValue),
        variable: tooltipVariable as EAggregatedDataType,
        detailedView: true,
        skipFormat,
        availableVariables,
      });
      const overallLabel =
        tooltipItem.dataset.data?.length > 1 && overallMaxValue
          ? `\n${overallMaxValue === 0 ? 0 : percentageFromMaxValue}% of total${hideSuffix ? '' : ` ${displayVariable}`}`
          : '';

      if (tooltipTitle) {
        if (typeof tooltipTitle === 'function') {
          return tooltipTitle([tooltipItem]);
        }
        return tooltipTitle;
      }

      return `${label}${hideSuffix ? '' : ` ${displayVariable}`}${overallLabel}`;
    },
    afterFooter() {
      return timeRange;
    },
  },
});

export const generateDoughnutKindTooltip = ({
  variable,
  overallMaxValue,
  breakdown,
  variableToDisplay,
  skipFormat,
  timeRange,
  tooltipTitle,
  tooltipAfterBody,
  isPercentage,
  isPercentageVariable,
  resolveTooltipVariable,
  colors,
  availableVariables,
  alias,
  enlargeBody,
}: {
  variable: string;
  overallMaxValue?: number;
  breakdown?: string;
  variableToDisplay?: string | TooltipTitleFn<'doughnut'>;
  timeRange: string;
  tooltipTitle?: TooltipTitle<'doughnut'>;
  tooltipAfterBody?: TooltipAfterBody<'doughnut'>;
  skipFormat?: EAggregatedDataType[];
  isPercentage?: boolean;
  isPercentageVariable?: (variable?: string) => boolean;
  resolveTooltipVariable?: (tooltipItem: TooltipItem<'doughnut'>) => string;
  colors?: string[];
  availableVariables?: GetAvailableVariables[];
  alias?: KindAliasFn;
  /** Render the tooltip body text larger (used for descriptive, multi-line tooltips). */
  enlargeBody?: boolean;
}): TooltipOptions<'doughnut'> => ({
  ...TOOLTIP_STYLES_ANALYTICS,
  ...(enlargeBody ? { bodyFont: TOOLTIP_BODY_FONT_LARGE } : {}),
  animation: {
    duration: TOOLTIP_ANIMATION_DURATION,
  },
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  callbacks: {
    beforeTitle(items): string | string[] | void {
      if (variableToDisplay) {
        if (typeof variableToDisplay === 'function') {
          return variableToDisplay(items);
        }
      }

      return `${variableToDisplay || 'Not Selected'}`;
    },
    title() {
      return '';
    },
    labelColor: (tooltipItem: TooltipItem<'doughnut'>) => {
      const index = tooltipItem.dataIndex;
      const finalColors = colors ? colors : MULTIPLE_COLORS;
      const datasetColor = tooltipItem.dataset.borderColor;
      const color = Array.isArray(datasetColor)
        ? datasetColor[index]
        : datasetColor || finalColors[index % finalColors.length];

      return {
        borderColor: color,
        backgroundColor: color,
        borderWidth: 2,
        borderRadius: 1,
      };

      // return {
      //   borderColor: MULTIPLE_COLORS[tooltipItem.datasetIndex % MULTIPLE_COLORS_AMOUNT],
      //   borderWidth: 2,
      //   borderRadius: 1,
      //   backgroundColor: MULTIPLE_COLORS[tooltipItem.datasetIndex % MULTIPLE_COLORS_AMOUNT],
      // };
    },
    label(tooltipItem: TooltipItem<'doughnut'>) {
      if (!breakdown) {
        return 'Total';
      }

      return ` ${capitalize(translate(breakdownToTKey(breakdown), tooltipItem.label))}`;
    },
    afterBody(items) {
      if (tooltipAfterBody && typeof tooltipAfterBody === 'function') {
        return tooltipAfterBody(items);
      }
      return `${breakdown ? (alias?.(breakdown) ?? `${breakdown}`) : ''}`;
    },
    footer([tooltipItem]: TooltipItem<'doughnut'>[]) {
      const currentValue = tooltipItem.dataset.data[tooltipItem.dataIndex] as number;
      const tooltipVariable = resolveTooltipVariable?.(tooltipItem) ?? variable;
      const showAsPercentage = isPercentage || (isPercentageVariable?.(tooltipVariable) ?? false);

      // If isPercentage is true, show only the percentage value
      if (showAsPercentage) {
        return formatPercentage(currentValue, { inputScale: 'decimal' });
      }

      const translatedVariable = translate('variable', tooltipVariable).toLowerCase();
      const availableVariablesBySlug = availableVariables
        ? new Map(availableVariables.map((entry) => [entry.slug, entry]))
        : null;
      const hideSuffix =
        !!availableVariablesBySlug && hasCustomVariableUnitSuffix(tooltipVariable, availableVariablesBySlug);
      const displayVariable =
        availableVariablesBySlug && !hideSuffix
          ? resolveCustomVariableLabel(tooltipVariable, availableVariablesBySlug, translatedVariable)
          : translatedVariable;
      const percentageFromMaxValue = formatPercentage((currentValue / (overallMaxValue ?? 1)) * 100, {
        inputScale: 'percent',
        showSymbol: false,
      });
      const label = formatChartLabel({
        label: String(currentValue),
        variable: tooltipVariable as EAggregatedDataType,
        detailedView: true,
        skipFormat,
        availableVariables,
      });
      const overallLabel =
        tooltipItem.dataset.data?.length > 1 && overallMaxValue
          ? `\n${overallMaxValue === 0 ? 0 : percentageFromMaxValue}% of total${hideSuffix ? '' : ` ${displayVariable}`}`
          : '';

      if (tooltipTitle) {
        if (typeof tooltipTitle === 'function') {
          return tooltipTitle([tooltipItem]);
        }
        return tooltipTitle;
      }

      return `${label}${hideSuffix ? '' : ` ${displayVariable}`}${overallLabel}`;
    },
    afterFooter() {
      return timeRange;
    },
  },
});

export const generateVerticalBarKindTooltip = ({
  variable,
  overallMaxValue,
  variableToDisplay,
  skipFormat,
  xAxisName,
  chartData,
  availableVariables,
}: {
  variable: string;
  xAxisName: string;
  overallMaxValue?: number;
  variableToDisplay?: string;
  skipFormat?: EAggregatedDataType[];
  chartData?: ChartDataset<'bar'>[];
  availableVariables?: GetAvailableVariables[];
}): TooltipOptions<'bar'> => ({
  ...TOOLTIP_STYLES_ANALYTICS,
  animation: {
    duration: TOOLTIP_ANIMATION_DURATION,
  },
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  callbacks: {
    beforeTitle(): string | string[] | void {
      return `${variableToDisplay || 'Not Selected'}`;
    },
    title() {
      return '';
    },
    labelColor: (tooltipItem: TooltipItem<'bar'>) => {
      const index = tooltipItem.dataIndex;
      const colors = tooltipItem.dataset.borderColor || [];
      // TODO: fix ignore
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const backgroundColor = colors[index] ?? '';

      return {
        borderColor: backgroundColor,
        backgroundColor: backgroundColor,
        borderWidth: 2,
        borderRadius: 1,
      };
    },
    label(tooltipItem: TooltipItem<'bar'>) {
      return `${tooltipItem.label} ${xAxisName}`;
    },
    afterBody([tooltipItems]: TooltipItem<'bar'>[]) {
      const datasetValue = chartData?.[0]?.data?.[tooltipItems.dataIndex];
      return datasetValue ? `${datasetValue} ${variableToDisplay}` : '';
    },
    footer([tooltipItems]: TooltipItem<'bar'>[]) {
      const translatedVariable = translate('variable', variable).toLowerCase();
      const availableVariablesBySlug = availableVariables
        ? new Map(availableVariables.map((entry) => [entry.slug, entry]))
        : null;
      const hideSuffix = !!availableVariablesBySlug && hasCustomVariableUnitSuffix(variable, availableVariablesBySlug);
      const displayVariable =
        availableVariablesBySlug && !hideSuffix
          ? resolveCustomVariableLabel(variable, availableVariablesBySlug, translatedVariable)
          : translatedVariable;
      const currentValue = tooltipItems.dataset.data[tooltipItems.dataIndex] as number;
      const percentageFromMaxValue = formatPercentage((currentValue / (overallMaxValue ?? 1)) * 100, {
        inputScale: 'percent',
        showSymbol: false,
      });
      const label = formatChartLabel({
        label: String(currentValue),
        variable: variable as EAggregatedDataType,
        detailedView: true,
        skipFormat,
        availableVariables,
      });
      const overallLabel =
        tooltipItems.dataset.data?.length > 1 && overallMaxValue
          ? `\n${overallMaxValue === 0 ? 0 : percentageFromMaxValue}% of total${hideSuffix ? '' : ` ${displayVariable}`}`
          : '';

      return `${label}${hideSuffix ? '' : ` ${displayVariable}`}${overallLabel}`;
    },
  },
});

export const generateClusteredBarTooltip = ({
  variables,
  overallMaxValue,
  timeRange,
  tooltipTitle,
  tooltipAfterBody,
  variableToDisplay,
  skipFormat,
  availableVariables,
}: {
  variables: string[];
  overallMaxValue?: number;
  variableToDisplay?: string | ((items: TooltipItem<'bar'>[]) => string);
  timeRange: string;
  tooltipTitle?: string | ((items: TooltipItem<'bar'>[]) => string);
  tooltipAfterBody?: TooltipAfterBody<'bar'>;
  skipFormat?: EAggregatedDataType[];
  availableVariables?: GetAvailableVariables[];
}): TooltipOptions<'bar'> => {
  const resolve = (
    v: string | ((items: TooltipItem<'bar'>[]) => string) | undefined,
    items: TooltipItem<'bar'>[],
  ): string | undefined => (typeof v === 'function' ? v(items) : v);

  return {
    ...TOOLTIP_STYLES_ANALYTICS,
    animation: { duration: TOOLTIP_ANIMATION_DURATION },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    callbacks: {
      beforeTitle(items) {
        const first = items?.[0];
        if (!first) return '';
        const i = first.dataIndex;
        const labels = first.chart?.data?.labels as (string | number)[] | undefined;
        return labels && i != null ? String(labels[i]) : String(first.label ?? '');
      },
      title() {
        return '';
      },
      labelColor(tooltipItem: TooltipItem<'bar'>) {
        const backgroundColor = tooltipItem.dataset.backgroundColor as string;
        return {
          borderColor: backgroundColor,
          backgroundColor,
          borderWidth: 2,
          borderRadius: 1,
        };
      },
      afterBody(items) {
        if (tooltipAfterBody && typeof tooltipAfterBody === 'function') {
          return tooltipAfterBody(items);
        }
        return '';
      },
      // IMPORTANT: Chart.js calls label(tooltipItem) — NO second arg.
      label(tooltipItem: TooltipItem<'bar'>) {
        // Always build a valid items array for user callbacks
        const items = (tooltipItem.chart?.tooltip?.dataPoints as TooltipItem<'bar'>[]) ?? [tooltipItem];

        const custom = resolve(variableToDisplay, items);
        if (custom) return ` ${String(custom)}`;

        const dsLabel = tooltipItem.dataset.label || variables?.[tooltipItem.datasetIndex] || '';
        return ` ${String(dsLabel)}`;
      },
      footer(items: TooltipItem<'bar'>[]) {
        const customTitle = resolve(tooltipTitle, items);
        if (customTitle) return String(customTitle);

        const [tooltipItem] = items;
        const datasetLabel = tooltipItem.dataset.label || variables?.[tooltipItem.datasetIndex] || '';
        const variableSlug = variables?.[tooltipItem.datasetIndex] || datasetLabel;
        const translatedVariable = translate('variable', datasetLabel).toLowerCase();
        const availableVariablesBySlug = availableVariables
          ? new Map(availableVariables.map((entry) => [entry.slug, entry]))
          : null;
        const hideSuffix =
          !!availableVariablesBySlug && hasCustomVariableUnitSuffix(variableSlug, availableVariablesBySlug);
        const displayVariable =
          availableVariablesBySlug && !hideSuffix
            ? resolveCustomVariableLabel(variableSlug, availableVariablesBySlug, translatedVariable)
            : translatedVariable;
        const currentValue = tooltipItem.parsed.y;
        const percentageFromMaxValue = formatPercentage((currentValue / (overallMaxValue || 1)) * 100, {
          inputScale: 'percent',
          showSymbol: false,
        });

        const label = formatChartLabel({
          label: String(currentValue),
          variable: variableSlug as EAggregatedDataType,
          detailedView: true,
          skipFormat,
          availableVariables,
        });

        const overallLabel =
          overallMaxValue && overallMaxValue > 0
            ? `\n${percentageFromMaxValue}% of max${hideSuffix ? '' : ` ${displayVariable}`}`
            : '';

        return `${label}${hideSuffix ? '' : ` ${displayVariable}`}${overallLabel}`;
      },
      afterFooter() {
        return timeRange;
      },
    },
  };
};
